import { useCallback, useEffect } from "react";
import {
    SearchScopeEnum,
    SearchItemDTO,
    SearchNodeTypeEnum,
    SearchFilterDTO,
    SearchSortDTO,
    ToggleItemDTO,
    SearchItemType,
} from "data/types";
import {
    setCurrentItemAction,
    setSearchItemAction,
    searchResetAction,
    searchFormResetAction,
    searchAbortAction,
} from "data/actions";
import { usePrevious } from "hooks";
import { produce } from "immer";
import { useDispatch } from "react-redux";
import { Dispatch } from "redux";
import isEqual from "lodash/isEqual";
import Constants from "data/constants";

export type SearchHookProps = {
    searchItem: SearchItemDTO | null;
    setSearchItem: React.Dispatch<React.SetStateAction<SearchItemDTO | null>>;
    toggles: { toggles: ToggleItemDTO[] } | null;
    setToggles: React.Dispatch<
        React.SetStateAction<{ toggles: ToggleItemDTO[] } | null>
    >;
    setCurrentRow: React.Dispatch<React.SetStateAction<number>>;
    viewName: string;
    searchFormId: number;
    searchForm: SearchItemDTO | null;
    crumbSearchItem: SearchItemDTO | null;
    setCrumbSearchItem: React.Dispatch<
        React.SetStateAction<SearchItemDTO | null>
    >;
    currentRow: number;
    setSource: (source: string) => void;
    searchResultStatus: string;
};

export type SearchHookReturnProps = [
    (
        searchText: string,
        scope: SearchScopeEnum,
        itemUri: string,
        filters: SearchFilterDTO[],
        sorts: SearchSortDTO[],
        nodeType: SearchNodeTypeEnum
    ) => void,
    () => void,
    () => void
];

const useSearch = ({
    searchItem,
    setSearchItem,
    toggles,
    setToggles,
    setCurrentRow,
    viewName,
    searchFormId,
    searchForm,
    crumbSearchItem,
    setCrumbSearchItem,
    currentRow,
    setSource,
    searchResultStatus,
}: SearchHookProps): SearchHookReturnProps => {
    const dispatch: Dispatch = useDispatch();
    const prevCurrentRow = usePrevious(currentRow) || currentRow;
    const prevSearchItem = usePrevious(searchItem);
    const prevSearchResultStatus = usePrevious(searchResultStatus);
    const prevSearchForm = usePrevious(searchForm);

    //#region callbacks
    const handleSearch = useCallback(
        (
            searchText: string,
            scope: SearchScopeEnum,
            itemUri: string,
            filters: SearchFilterDTO[],
            sorts: SearchSortDTO[],
            nodeType: SearchNodeTypeEnum
        ) => {
            dispatch(searchAbortAction());
            const searchItem: SearchItemDTO = {
                id: -1,
                name:
                    typeof searchText !== "string" || !searchText
                        ? "N/A"
                        : searchText,
                searchText,
                searchType: SearchItemType.Freetext,
                itemUri,
                scope,
                filters: filters || [],
                sorts: sorts || [],
                nodeType,
            };
            setSearchItem(searchItem);
        },
        [dispatch, setSearchItem]
    );

    const updateResultToggle = useCallback(
        (show: boolean) => {
            /* istanbul ignore if */
            if (toggles == null) return;

            // disable result toggle when isFlatAU
            const newToggles = produce(toggles, (draft) => {
                draft.toggles.forEach((toggle: ToggleItemDTO) => {
                    if (toggle.value === "result") {
                        toggle.isDisabled = !show;
                        toggle.isSelected = show;
                    }
                });
            });
            /* istanbul ignore else */
            newToggles != null && setToggles(newToggles);
        },
        [toggles, setToggles]
    );

    const clearSearchResults = useCallback(
        () => dispatch(searchResetAction()),
        [dispatch]
    );

    const handleSearchFormCancel = useCallback(() => {
        dispatch(searchAbortAction());
        dispatch(searchFormResetAction());
        setCurrentRow(-1);
    }, [setCurrentRow, dispatch]);
    //#endregion

    //#region effects
    useEffect(() => {
        dispatch(
            setCurrentItemAction({
                name: viewName,
                itemUri: "",
            })
        );
    }, [dispatch, viewName]);

    // set crumbSearchItem when searchForm is selected
    useEffect(() => {
        if (
            searchFormId === -1 ||
            searchForm == null ||
            prevSearchForm === searchForm
        )
            return;
        setCrumbSearchItem({
            id: searchForm.id,
            name: searchForm.name,
            searchType: SearchItemType.Form,
        });
        dispatch(searchResetAction());
        updateResultToggle(false);
    }, [
        searchFormId,
        searchForm,
        prevSearchForm,
        updateResultToggle,
        dispatch,
        setCrumbSearchItem,
    ]);

    // update result toggle when currentRow changes
    useEffect(() => {
        if (prevCurrentRow === currentRow) return;
        updateResultToggle(false);
    }, [prevCurrentRow, currentRow, updateResultToggle]);

    // reset crumbSearchItem when currentRow resets
    useEffect(() => {
        if (currentRow !== -1 || crumbSearchItem == null) return;
        setCrumbSearchItem(null);
    }, [currentRow, crumbSearchItem, setCrumbSearchItem]);

    // communicate source to parent
    useEffect(() => {
        if (searchItem == null || isEqual(prevSearchItem, searchItem)) return;
        setSource?.(
            `${searchItem.id}|${searchItem.name}|${JSON.stringify(
                searchItem.searchText
            )}`
        );
    }, [searchItem, prevSearchItem, setSource]);

    useEffect(() => {
        if (searchForm || searchItem == null || searchItem.id === -1) return;
        setSearchItem(null);
    }, [searchForm, searchItem, setSearchItem]);

    useEffect(() => {
        if (prevSearchResultStatus === searchResultStatus) return;
        updateResultToggle(searchResultStatus !== Constants.NONE);
    }, [prevSearchResultStatus, searchResultStatus, updateResultToggle]);

    useEffect(() => {
        if (searchItem == null || isEqual(prevSearchItem, searchItem)) return;
        // by dispatching this we enable the sidebar/searchResults view
        dispatch(setSearchItemAction({ searchItem }));
    }, [searchItem, prevSearchItem, dispatch]);
    //#endregion

    return [handleSearch, clearSearchResults, handleSearchFormCancel];
};

export { useSearch };
